import { connect } from 'react-redux';
import autobind from 'core-decorators/lib/autobind';
import classnames from 'classnames';
import { FormattedMessage, FormattedNumber, FormattedDate } from 'react-intl';

import { VODCSSModules } from 'shared/utils/wix-connect';

import { areElementsRounded, canShowFullInfo, canShareVideo, getSiteUrl, getCompId } from 'shared/selectors/app-settings';
import getRentStatusConfig from './selectors/rent-status';
import getPurchaseStatusConfig from './selectors/purchase-status';
import getPaidAccessButtonConfigForVideo from 'widget/views/desktop/compact/components/player-overlay/ui-selectors/video-overlay/paid-access/button-config';

import Title from './title/title';
import ActionButton from './action-button/action-button';
import ClosableOverlay from './closable-overlay/closable-overlay';
import VideoInfo from './info/info';
import ShareVideo from './share/share';
import Animate from 'widget/views/desktop/compact/components/animate/animate';

import styles from './ui-wrapper.styl';


const INFO_OVERLAY_CONTENT = {
  VIDEO_INFO: 'videoInfo',
  SHARE_VIDEO: 'shareVideo',
};

@connect(state => ({
  siteUrl: getSiteUrl(state),
  compId: getCompId(state),
  areElementsRounded: areElementsRounded(state),
  canShowFullInfo: canShowFullInfo(state),
  canShareVideo: canShareVideo(state),
}))
@VODCSSModules(styles)
export default class UIWrapper extends React.Component {

  static propTypes = {
    appSettings: React.PropTypes.object,
    channelData: React.PropTypes.object,
    videoItem: React.PropTypes.object,

    onSubscribeRequest: React.PropTypes.func,
    onPurchaseRequest: React.PropTypes.func,
    onShareRequest: React.PropTypes.func,
    onVideoInfoShow: React.PropTypes.func,
    onShareVideoShow: React.PropTypes.func,

    siteUrl: React.PropTypes.string,
    compId: React.PropTypes.string,
    areElementsRounded: React.PropTypes.bool,
    canShowFullInfo: React.PropTypes.bool,
    canShareVideo: React.PropTypes.bool,

    isVideoPaused: React.PropTypes.bool,
    isVideoEnded: React.PropTypes.bool,
    isControlsShown: React.PropTypes.bool,
    isPlayerInFullScreen: React.PropTypes.bool,
    exitFullScreen: React.PropTypes.func,
    pauseVideo: React.PropTypes.func,
  };

  static defaultProps = {
    onSubscribeRequest: _.noop,
    onPurchaseRequest: _.noop,
    onShareRequest: _.noop,
    onVideoInfoShow: _.noop,
    onShareVideoShow: _.noop,
  };

  constructor(props) {
    super(props);

    this._hideTimeout = null;

    this.state = {
      isMainContentHidden: props.isControlsShown,
      overlayContent: null,
    };
  }

  componentWillReceiveProps(nextProps) {
    this.shouldMainContentBeHidden(nextProps);
    this.shouldCloseInfoOverlay(nextProps);
  }

  shouldMainContentBeHidden(nextProps) {
    const { isMainContentHidden } = this.state;
    const { isControlsShown } = nextProps;

    if (isControlsShown) {
      if (isMainContentHidden) {
        this.show();
      }
    } else {
      this.hide();
    }
  }

  shouldCloseInfoOverlay(nextProps) {
    const { videoItem: { id }, isVideoEnded } = nextProps;

    if (this.props.videoItem.id !== id || isVideoEnded) {
      this.resetOverlayContent();
    }
  }

  @autobind
  show() {
    this.setState({
      isMainContentHidden: false,
    });
  }

  @autobind
  hide() {
    this.setState({
      isMainContentHidden: true,
    });
  }

  @autobind
  showVideoInfoOverlay() {
    const { onVideoInfoShow } = this.props;

    this.setState({
      overlayContent: INFO_OVERLAY_CONTENT.VIDEO_INFO,
    });

    onVideoInfoShow();
  }

  @autobind
  showShareVideoOverlay() {
    const { onShareVideoShow } = this.props;

    this.setState({
      overlayContent: INFO_OVERLAY_CONTENT.SHARE_VIDEO,
    });

    onShareVideoShow();
  }

  @autobind
  resetOverlayContent() {
    this.setState({
      overlayContent: null,
    });
  }

  @autobind
  proxySubscribeRequest() {
    const { onSubscribeRequest, pauseVideo, exitFullScreen } = this.props;

    exitFullScreen();
    pauseVideo();
    onSubscribeRequest();
  }

  @autobind
  proxyPurchaseRequest() {
    const { onPurchaseRequest, pauseVideo, exitFullScreen } = this.props;

    exitFullScreen();
    pauseVideo();
    onPurchaseRequest();
  }

  renderActionButton(icon, label, onClick, className) {
    const { areElementsRounded, isPlayerInFullScreen } = this.props;

    return (
      <ActionButton styleName="action-button"
                    className={className}
                    icon={icon}
                    label={label}
                    onClick={onClick}
                    isInFullScreen={isPlayerInFullScreen}
                    rounded={areElementsRounded} />
    );
  }

  get rentStatus() {
    const { videoItem } = this.props;
    const rentStatusConfig = getRentStatusConfig(videoItem);

    if (!rentStatusConfig) {
      return null;
    }

    const { translationData: { expirationDate, props } } = rentStatusConfig;

    const date = (
      <FormattedDate value={expirationDate}
                     month="short"
                     day="numeric" />
    );

    const label = (
      <FormattedMessage {...props}
                        values={{ date }} />
    );

    return this.renderActionButton('timer', label);
  }

  get purchaseStatus() {
    const { videoItem } = this.props;
    const purchaseStatusConfig = getPurchaseStatusConfig(videoItem);

    if (!purchaseStatusConfig) {
      return null;
    }

    const { translationData: { props } } = purchaseStatusConfig;

    const label = (
      <FormattedMessage {...props} />
    );

    return this.renderActionButton('film', label);
  }

  get paidAccessAction() {
    const { videoItem, channelData } = this.props;
    const paidAccessButtonConfig = getPaidAccessButtonConfigForVideo({
      videoItem,
      channelData,
      onPurchaseRequest: this.proxyPurchaseRequest,
      onSubscriptionRequest: this.proxySubscribeRequest,
    });

    if (!paidAccessButtonConfig) {
      return null;
    }

    const { translationData: { priceModel, props }, callback } = paidAccessButtonConfig;

    const price = (
      <FormattedNumber value={priceModel.price}
                       style="currency"
                       currency={priceModel.currency} />
    );

    const label = (
      <FormattedMessage {...props}
                        values={{ price }} />
    );

    return this.renderActionButton('buy-rent', label, callback, 'qa-widget-overlay-buy-rent-button');
  }

  get videoAccessIndicator() {
    return this.purchaseStatus || this.rentStatus || this.paidAccessAction;
  }

  get infoAction() {
    const { canShowFullInfo } = this.props;

    if (!canShowFullInfo) {
      return null;
    }

    return this.renderActionButton('info', null, this.showVideoInfoOverlay, 'qa-widget-overlay-info-button');
  }

  get shareAction() {
    const { canShareVideo } = this.props;

    if (!canShareVideo) {
      return null;
    }

    return this.renderActionButton('share', null, this.showShareVideoOverlay, 'qa-widget-overlay-share-button');
  }

  get mainContent() {
    const { videoItem: { title }, isPlayerInFullScreen } = this.props;
    const { isMainContentHidden } = this.state;

    const styleNames = classnames('main-content-inner', {
      'at-full-screen': isPlayerInFullScreen,
    });


    return (
      <section styleName="main-content-wrapper">
        <Animate type="fade-in-out"
                 styleName="main-content"
                 when={!isMainContentHidden}>
          <div styleName={styleNames}>
            <Title title={title}
                   isInFullScreen={isPlayerInFullScreen} />
            <div styleName="actions"
                 className="qa-player-overlay-actions">
              {this.videoAccessIndicator}
              {this.infoAction}
              {this.shareAction}
            </div>
          </div>
        </Animate>
      </section>
    );
  }

  get overlay() {
    const { overlayContent } = this.state;
    const {
      videoItem: {
        id,
        posterUrl,
        coverUrl,
        title,
        description,
        publisher,
        durationStr,
        genre,
        cast,
        crew,
      },
      onShareRequest,
      isPlayerInFullScreen,
      siteUrl,
      compId,
      areElementsRounded,
    } = this.props;

    const styleNames = classnames('info-overlay-wrapper', {
      active: overlayContent,
    });

    return (
      <section styleName={styleNames}>
        <Animate type="fade-in-out"
                 styleName="info-overlay"
                 when={overlayContent === INFO_OVERLAY_CONTENT.VIDEO_INFO}>
          <ClosableOverlay onClose={this.resetOverlayContent}
                           isInFullScreen={isPlayerInFullScreen}
                           posterUrl={posterUrl}>
            <VideoInfo title={title}
                       description={description}
                       posterUrl={posterUrl}
                       publisher={publisher}
                       genre={genre}
                       durationStr={durationStr}
                       cast={cast}
                       crew={crew}
                       isInFullScreen={isPlayerInFullScreen} />
          </ClosableOverlay>
        </Animate>
        <Animate type="fade-in-out"
                 styleName="info-overlay"
                 when={overlayContent === INFO_OVERLAY_CONTENT.SHARE_VIDEO}>
          <ClosableOverlay onClose={this.resetOverlayContent}
                           isInFullScreen={isPlayerInFullScreen}
                           closeOnBodyClick
                           isFullWidth>
            <ShareVideo id={id}
                        title={title}
                        description={description}
                        coverUrl={coverUrl}
                        siteUrl={siteUrl}
                        compId={compId}
                        areElementsRounded={areElementsRounded}
                        onShare={onShareRequest}
                        isInFullScreen={isPlayerInFullScreen} />
          </ClosableOverlay>
        </Animate>
      </section>
    );
  }

  render() {
    return (
      <section>
        {this.mainContent}
        {this.overlay}
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/player-widget/components/new-player/components/ui-wrapper.js